import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  ITabletSubmission,
  ITabletSubmissionPayload,
} from '../../Interfaces/ITabletSubmission';
import { customFetcher } from '../useFetch';

const SUBMISSIONS_KEY = ['tabletsubmission'];

const submissionsForTransactionKey = (idLegalTransaction: number) => [
  ...SUBMISSIONS_KEY,
  { idLegalTransaction },
];

const POLL_INTERVAL_MS = 5_000;

export const useGetTabletSubmissionsForLegalTransaction = (
  idLegalTransaction: number | null | undefined
) => {
  const enabled = idLegalTransaction != null;
  const {
    data: submissions,
    isLoading: isLoadingSubmissions,
    refetch: refetchSubmissions,
  } = useQuery<ITabletSubmission[]>({
    queryKey: submissionsForTransactionKey(idLegalTransaction ?? 0),
    queryFn: () =>
      customFetcher({
        endpoint: `/tabletsubmission?idLegalTransaction=${idLegalTransaction}`,
      }),
    enabled,
    refetchInterval: enabled ? POLL_INTERVAL_MS : false,
    refetchIntervalInBackground: false,
  });
  return { submissions, isLoadingSubmissions, refetchSubmissions };
};

export const useCreateTabletSubmission = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: createSubmission,
    isPending: isPendingCreateSubmission,
  } = useMutation({
    mutationFn: (body: {
      idTabletDevice: number;
      idLegalTransaction: number;
      idLegalClient?: number;
      Language?: 'de' | 'en';
      EmailRequired?: boolean;
      PrefillPayload?: ITabletSubmissionPayload;
    }) =>
      customFetcher({
        endpoint: '/tabletsubmission',
        method: 'POST',
        body,
      }) as Promise<ITabletSubmission>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUBMISSIONS_KEY });
    },
  });
  return { createSubmission, isPendingCreateSubmission };
};

const submissionAction =
  (suffix: 'accept' | 'reject' | 'cancel') => (idTabletSubmission: number) =>
    customFetcher({
      endpoint: `/tabletsubmission/${idTabletSubmission}/${suffix}`,
      method: 'POST',
    });

export const useAcceptTabletSubmission = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: acceptSubmission,
    isPending: isPendingAcceptSubmission,
  } = useMutation({
    mutationFn: submissionAction('accept'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUBMISSIONS_KEY });
    },
  });
  return { acceptSubmission, isPendingAcceptSubmission };
};

export const useRejectTabletSubmission = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: rejectSubmission,
    isPending: isPendingRejectSubmission,
  } = useMutation({
    mutationFn: submissionAction('reject'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUBMISSIONS_KEY });
    },
  });
  return { rejectSubmission, isPendingRejectSubmission };
};

export const useCancelTabletSubmission = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: cancelSubmission,
    isPending: isPendingCancelSubmission,
  } = useMutation({
    mutationFn: submissionAction('cancel'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUBMISSIONS_KEY });
    },
  });
  return { cancelSubmission, isPendingCancelSubmission };
};

export const useMergeTabletSubmission = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: mergeIntoLegalClient,
    isPending: isPendingMergeIntoLegalClient,
  } = useMutation({
    mutationFn: ({
      idTabletSubmission,
      idLegalClient,
    }: {
      idTabletSubmission: number;
      idLegalClient: number;
    }) =>
      customFetcher({
        endpoint: `/tabletsubmission/${idTabletSubmission}/mergeIntoLegalClient`,
        method: 'POST',
        body: { idLegalClient },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUBMISSIONS_KEY });
      queryClient.invalidateQueries({ queryKey: ['legalClient'] });
      queryClient.invalidateQueries({ queryKey: ['legalTransaction'] });
    },
  });
  return { mergeIntoLegalClient, isPendingMergeIntoLegalClient };
};

export const useCreateLegalClientFromSubmission = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: createLegalClientFromSubmission,
    isPending: isPendingCreateLegalClient,
  } = useMutation({
    mutationFn: (idTabletSubmission: number) =>
      customFetcher({
        endpoint: `/tabletsubmission/${idTabletSubmission}/createLegalClient`,
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUBMISSIONS_KEY });
      queryClient.invalidateQueries({ queryKey: ['legalClient'] });
      queryClient.invalidateQueries({ queryKey: ['legalTransaction'] });
    },
  });
  return { createLegalClientFromSubmission, isPendingCreateLegalClient };
};
